/**
 * Options WebSocket Market Streams
 *
 * Access market data, manage accounts, and trade Binance Options.
 *
 * The version of the OpenAPI document: 1.0.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import {
    WebsocketStreamsBase,
    WebsocketStream,
    assertParamExists,
    replaceWebsocketStreamsPlaceholders,
    createStreamHandler,
} from '@binance/common';
import type {
    IndexPriceStreamsResponse,
    KlineCandlestickStreamsResponse,
    NewSymbolInfoResponse,
    OpenInterestResponse,
    OptionMarkPriceResponse,
} from '../types';

const MarketApiParamCreator = function () {
    return {
        /**
         * Underlying(e.g ETHUSDT) index stream.
         *
         * Update Speed: 1000ms
         *
         * @summary Index Price Streams
         * @param {number} [id] Unique WebSocket request ID.
         *
         * @throws {RequiredError}
         */
        indexPriceStreams: (id?: number): string => {
            return replaceWebsocketStreamsPlaceholders('/!index@arr'.slice(1), { id });
        },
        /**
         * The Kline/Candlestick Stream push updates to the current klines/candlestick every 1000 milliseconds (if existing).
         *
         * Update Speed: 1000ms
         *
         * @summary Kline/Candlestick Streams
         * @param {string} symbol The symbol parameter
         * @param {KlineCandlestickStreamsIntervalEnum} interval The interval parameter
         * @param {number} [id] Unique WebSocket request ID.
         *
         * @throws {RequiredError}
         */
        klineCandlestickStreams: (
            symbol: string,
            interval: KlineCandlestickStreamsIntervalEnum,
            id?: number
        ): string => {
            // verify required parameter 'symbol' is not null or undefined
            assertParamExists('klineCandlestickStreams', 'symbol', symbol);
            // verify required parameter 'interval' is not null or undefined
            assertParamExists('klineCandlestickStreams', 'interval', interval);

            return replaceWebsocketStreamsPlaceholders('/<symbol>@kline_<interval>'.slice(1), {
                symbol,
                interval,
                id,
            });
        },
        /**
         * New symbol listing stream.
         *
         * Update Speed: 50ms
         *
         * @summary New Symbol Info
         * @param {number} [id] Unique WebSocket request ID.
         *
         * @throws {RequiredError}
         */
        newSymbolInfo: (id?: number): string => {
            return replaceWebsocketStreamsPlaceholders('/!optionSymbol'.slice(1), { id });
        },
        /**
         * Option open interest for specific underlying asset on specific expiration date. E.g.[ethusdt@openInterest@221125](wss://fstream.binance.com/market/stream?streams=ethusdt@openInterest@221125)
         *
         * Update Speed: 60s
         *
         * @summary Open Interest
         * @param {string} underlying The underlying parameter
         * @param {string} expirationDate The expirationDate parameter
         * @param {number} [id] Unique WebSocket request ID.
         *
         * @throws {RequiredError}
         */
        openInterest: (underlying: string, expirationDate: string, id?: number): string => {
            // verify required parameter 'underlying' is not null or undefined
            assertParamExists('openInterest', 'underlying', underlying);
            // verify required parameter 'expirationDate' is not null or undefined
            assertParamExists('openInterest', 'expirationDate', expirationDate);

            return replaceWebsocketStreamsPlaceholders(
                '/<underlying>@openInterest@<expirationDate>'.slice(1),
                { underlying, expirationDate, id }
            );
        },
        /**
         * The mark price for all option symbols on specific underlying asset. E.g.[btcusdt@optionMarkPrice](wss://fstream.binance.com/market/stream?streams=btcusdt@optionMarkPrice)
         *
         * Update Speed: 1000ms
         *
         * @summary Option Mark Price
         * @param {string} underlying The underlying parameter
         * @param {number} [id] Unique WebSocket request ID.
         *
         * @throws {RequiredError}
         */
        optionMarkPrice: (underlying: string, id?: number): string => {
            // verify required parameter 'underlying' is not null or undefined
            assertParamExists('optionMarkPrice', 'underlying', underlying);

            return replaceWebsocketStreamsPlaceholders('/<underlying>@optionMarkPrice'.slice(1), {
                underlying,
                id,
            });
        },
    };
};

/**
 * MarketApi - interface
 * @interface MarketApi
 */
export interface MarketApiInterface {
    /**
     * Underlying(e.g ETHUSDT) index stream.
     *
     * Update Speed: 1000ms
     *
     * @summary Index Price Streams
     * @param {IndexPriceStreamsRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<IndexPriceStreamsResponse>}
     * @throws {RequiredError}
     * @memberof MarketApiInterface
     */
    indexPriceStreams(
        requestParameters?: IndexPriceStreamsRequest
    ): WebsocketStream<IndexPriceStreamsResponse>;

    /**
     * The Kline/Candlestick Stream push updates to the current klines/candlestick every 1000 milliseconds (if existing).
     *
     * Update Speed: 1000ms
     *
     * @summary Kline/Candlestick Streams
     * @param {KlineCandlestickStreamsRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<KlineCandlestickStreamsResponse>}
     * @throws {RequiredError}
     * @memberof MarketApiInterface
     */
    klineCandlestickStreams(
        requestParameters: KlineCandlestickStreamsRequest
    ): WebsocketStream<KlineCandlestickStreamsResponse>;

    /**
     * New symbol listing stream.
     *
     * Update Speed: 50ms
     *
     * @summary New Symbol Info
     * @param {NewSymbolInfoRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<NewSymbolInfoResponse>}
     * @throws {RequiredError}
     * @memberof MarketApiInterface
     */
    newSymbolInfo(requestParameters?: NewSymbolInfoRequest): WebsocketStream<NewSymbolInfoResponse>;

    /**
     * Option open interest for specific underlying asset on specific expiration date. E.g.[ethusdt@openInterest@221125](wss://fstream.binance.com/market/stream?streams=ethusdt@openInterest@221125)
     *
     * Update Speed: 60s
     *
     * @summary Open Interest
     * @param {OpenInterestRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<OpenInterestResponse>}
     * @throws {RequiredError}
     * @memberof MarketApiInterface
     */
    openInterest(requestParameters: OpenInterestRequest): WebsocketStream<OpenInterestResponse>;

    /**
     * The mark price for all option symbols on specific underlying asset. E.g.[btcusdt@optionMarkPrice](wss://fstream.binance.com/market/stream?streams=btcusdt@optionMarkPrice)
     *
     * Update Speed: 1000ms
     *
     * @summary Option Mark Price
     * @param {OptionMarkPriceRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<OptionMarkPriceResponse>}
     * @throws {RequiredError}
     * @memberof MarketApiInterface
     */
    optionMarkPrice(
        requestParameters: OptionMarkPriceRequest
    ): WebsocketStream<OptionMarkPriceResponse>;
}

/**
 * Request parameters for indexPriceStreams operation in MarketApi.
 * @interface IndexPriceStreamsRequest
 */
export interface IndexPriceStreamsRequest {
    /**
     * Unique WebSocket request ID.
     * @type {number}
     * @memberof MarketApiIndexPriceStreams
     */
    readonly id?: number;
}

/**
 * Request parameters for klineCandlestickStreams operation in MarketApi.
 * @interface KlineCandlestickStreamsRequest
 */
export interface KlineCandlestickStreamsRequest {
    /**
     * The symbol parameter
     * @type {string}
     * @memberof MarketApiKlineCandlestickStreams
     */
    readonly symbol: string;

    /**
     * The interval parameter
     * @type {'1m' | '3m' | '5m' | '15m' | '30m' | '1h' | '2h' | '4h' | '6h' | '12h' | '1d' | '3d' | '1w'}
     * @memberof MarketApiKlineCandlestickStreams
     */
    readonly interval: KlineCandlestickStreamsIntervalEnum;

    /**
     * Unique WebSocket request ID.
     * @type {number}
     * @memberof MarketApiKlineCandlestickStreams
     */
    readonly id?: number;
}

/**
 * Request parameters for newSymbolInfo operation in MarketApi.
 * @interface NewSymbolInfoRequest
 */
export interface NewSymbolInfoRequest {
    /**
     * Unique WebSocket request ID.
     * @type {number}
     * @memberof MarketApiNewSymbolInfo
     */
    readonly id?: number;
}

/**
 * Request parameters for openInterest operation in MarketApi.
 * @interface OpenInterestRequest
 */
export interface OpenInterestRequest {
    /**
     * The underlying parameter
     * @type {string}
     * @memberof MarketApiOpenInterest
     */
    readonly underlying: string;

    /**
     * The expirationDate parameter
     * @type {string}
     * @memberof MarketApiOpenInterest
     */
    readonly expirationDate: string;

    /**
     * Unique WebSocket request ID.
     * @type {number}
     * @memberof MarketApiOpenInterest
     */
    readonly id?: number;
}

/**
 * Request parameters for optionMarkPrice operation in MarketApi.
 * @interface OptionMarkPriceRequest
 */
export interface OptionMarkPriceRequest {
    /**
     * The underlying parameter
     * @type {string}
     * @memberof MarketApiOptionMarkPrice
     */
    readonly underlying: string;

    /**
     * Unique WebSocket request ID.
     * @type {number}
     * @memberof MarketApiOptionMarkPrice
     */
    readonly id?: number;
}

/**
 * MarketApi - interface
 * @class MarketApi
 * @extends {WebsocketStreamsBase}
 */
export class MarketApi implements MarketApiInterface {
    private readonly websocketBase: WebsocketStreamsBase;
    private localVarParamCreator;

    constructor(websocketBase: WebsocketStreamsBase) {
        this.websocketBase = websocketBase;
        this.localVarParamCreator = MarketApiParamCreator();
    }

    /**
     * Underlying(e.g ETHUSDT) index stream.
     *
     * Update Speed: 1000ms
     *
     * @summary Index Price Streams
     * @param {IndexPriceStreamsRequest} requestParameters Request parameters.
     * @returns {WebsocketStream<IndexPriceStreamsResponse>}
     * @throws {RequiredError}
     * @memberof MarketApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-options/api/ws-streams/market#index-price-streams Binance API Documentation}
     */
    public indexPriceStreams(
        requestParameters: IndexPriceStreamsRequest = {}
    ): WebsocketStream<IndexPriceStreamsResponse> {
        const stream = this.localVarParamCreator.indexPriceStreams(requestParameters?.id);

        return createStreamHandler<IndexPriceStreamsResponse>(
            this.websocketBase,
            stream,
            requestParameters?.id,
            'market'
        );
    }

    /**
     * The Kline/Candlestick Stream push updates to the current klines/candlestick every 1000 milliseconds (if existing).
     *
     * Update Speed: 1000ms
     *
     * @summary Kline/Candlestick Streams
     * @param {KlineCandlestickStreamsRequest} requestParameters Request parameters.
     * @returns {WebsocketStream<KlineCandlestickStreamsResponse>}
     * @throws {RequiredError}
     * @memberof MarketApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-options/api/ws-streams/market#kline-candlestick-streams Binance API Documentation}
     */
    public klineCandlestickStreams(
        requestParameters: KlineCandlestickStreamsRequest
    ): WebsocketStream<KlineCandlestickStreamsResponse> {
        const stream = this.localVarParamCreator.klineCandlestickStreams(
            requestParameters?.symbol,
            requestParameters?.interval,
            requestParameters?.id
        );

        return createStreamHandler<KlineCandlestickStreamsResponse>(
            this.websocketBase,
            stream,
            requestParameters?.id,
            'market'
        );
    }

    /**
     * New symbol listing stream.
     *
     * Update Speed: 50ms
     *
     * @summary New Symbol Info
     * @param {NewSymbolInfoRequest} requestParameters Request parameters.
     * @returns {WebsocketStream<NewSymbolInfoResponse>}
     * @throws {RequiredError}
     * @memberof MarketApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-options/api/ws-streams/market#new-symbol-info Binance API Documentation}
     */
    public newSymbolInfo(
        requestParameters: NewSymbolInfoRequest = {}
    ): WebsocketStream<NewSymbolInfoResponse> {
        const stream = this.localVarParamCreator.newSymbolInfo(requestParameters?.id);

        return createStreamHandler<NewSymbolInfoResponse>(
            this.websocketBase,
            stream,
            requestParameters?.id,
            'market'
        );
    }

    /**
     * Option open interest for specific underlying asset on specific expiration date. E.g.[ethusdt@openInterest@221125](wss://fstream.binance.com/market/stream?streams=ethusdt@openInterest@221125)
     *
     * Update Speed: 60s
     *
     * @summary Open Interest
     * @param {OpenInterestRequest} requestParameters Request parameters.
     * @returns {WebsocketStream<OpenInterestResponse>}
     * @throws {RequiredError}
     * @memberof MarketApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-options/api/ws-streams/market#open-interest Binance API Documentation}
     */
    public openInterest(
        requestParameters: OpenInterestRequest
    ): WebsocketStream<OpenInterestResponse> {
        const stream = this.localVarParamCreator.openInterest(
            requestParameters?.underlying,
            requestParameters?.expirationDate,
            requestParameters?.id
        );

        return createStreamHandler<OpenInterestResponse>(
            this.websocketBase,
            stream,
            requestParameters?.id,
            'market'
        );
    }

    /**
     * The mark price for all option symbols on specific underlying asset. E.g.[btcusdt@optionMarkPrice](wss://fstream.binance.com/market/stream?streams=btcusdt@optionMarkPrice)
     *
     * Update Speed: 1000ms
     *
     * @summary Option Mark Price
     * @param {OptionMarkPriceRequest} requestParameters Request parameters.
     * @returns {WebsocketStream<OptionMarkPriceResponse>}
     * @throws {RequiredError}
     * @memberof MarketApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-options/api/ws-streams/market#option-mark-price Binance API Documentation}
     */
    public optionMarkPrice(
        requestParameters: OptionMarkPriceRequest
    ): WebsocketStream<OptionMarkPriceResponse> {
        const stream = this.localVarParamCreator.optionMarkPrice(
            requestParameters?.underlying,
            requestParameters?.id
        );

        return createStreamHandler<OptionMarkPriceResponse>(
            this.websocketBase,
            stream,
            requestParameters?.id,
            'market'
        );
    }
}

export enum KlineCandlestickStreamsIntervalEnum {
    INTERVAL_1m = '1m',
    INTERVAL_3m = '3m',
    INTERVAL_5m = '5m',
    INTERVAL_15m = '15m',
    INTERVAL_30m = '30m',
    INTERVAL_1h = '1h',
    INTERVAL_2h = '2h',
    INTERVAL_4h = '4h',
    INTERVAL_6h = '6h',
    INTERVAL_12h = '12h',
    INTERVAL_1d = '1d',
    INTERVAL_3d = '3d',
    INTERVAL_1w = '1w',
}
